// "الشهادات" — the certificates for each edition's students. Sales list who
// finished the run (names as they must be printed) and so ask the designer for
// the certificates; the designer uploads one per student, or shares a single
// folder link holding them all, and delivers; sales can send them back for
// changes. One request per edition.
//
// Who may write which half is in src/lib/roles.ts, the status moves in
// src/lib/degrees.ts; both are enforced again by `degrees.*` and the upload
// route — the hidden buttons here are convenience.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	CalendarClock,
	Check,
	ClipboardPaste,
	Download,
	ExternalLink,
	FileCheck2,
	FolderOpen,
	Loader2,
	Pencil,
	Plus,
	Trash2,
	Upload,
	UserCheck,
	UserMinus,
	Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { GhostButton, Modal, PrimaryButton } from "#/components/admin/modal";
import { TabBar, type TabItem } from "#/components/admin/tabs";
import { authClient } from "#/lib/auth-client";
import {
	DEGREE_ACCEPT_ATTR,
	DEGREE_STATUS_LABELS,
	type DegreeStatus,
	degreeActions,
	degreeWaitsOn,
	parseBirthDate,
	parseStudentLines,
	validateDegreeUpload,
} from "#/lib/degrees";
import { editionLabel, isoDay } from "#/lib/editions";
import { isPreviewable } from "#/lib/fiches";
import { algiersToday } from "#/lib/lead-filters";
import {
	canEditDegreeFiles,
	canEditDegreeStudents,
	canViewDegrees,
	homeRouteFor,
	isDesigner,
} from "#/lib/roles";
import { getSession } from "#/lib/session";
import { formatBytes } from "#/lib/uploads";
import { dayLabel } from "#/lib/weekly-report";

const FILTERS = [
	"mine",
	"all",
	"REQUESTED",
	"IN_PROGRESS",
	"DELIVERED",
	"none",
] as const;
type Filter = (typeof FILTERS)[number];

const searchSchema = z.object({
	filter: z.enum(FILTERS).optional(),
	// The open edition — a link sales can send the designer straight to.
	edition: z.coerce.number().int().optional(),
});

type DegreesSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/degrees")({
	validateSearch: (search: Record<string, unknown>): DegreesSearch => {
		const parsed = searchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewDegrees(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: DegreesPage,
});

const CARD = "rounded-2xl border border-line bg-white shadow-card";
const INPUT =
	"w-full rounded-xl border border-line bg-white px-3 py-2 text-sm font-normal text-navy outline-none focus:border-teal";

const STATUS_STYLES: Record<DegreeStatus | "none", string> = {
	REQUESTED: "bg-amber-50 text-amber-800",
	IN_PROGRESS: "bg-teal-50 text-teal-600",
	DELIVERED: "bg-emerald-50 text-emerald-700",
	none: "bg-canvas text-muted",
};

function StatusBadge({ status }: { status: DegreeStatus | null }) {
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[status ?? "none"]}`}
		>
			{status ? DEGREE_STATUS_LABELS[status] : "لا توجد قائمة"}
		</span>
	);
}

function formatDateTime(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeStyle: "short",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

function runLabel(start: string | Date, end: string | Date) {
	const from = isoDay(start);
	const to = isoDay(end);
	return from === to ? dayLabel(from) : `${dayLabel(from)} ← ${dayLabel(to)}`;
}

function DegreesPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	const navigate = useNavigate({ from: Route.fullPath });
	const search = Route.useSearch();
	// The designer opens on their queue; sales and admins on every run, since
	// a run with no list yet is theirs to start.
	const defaultFilter: Filter = isDesigner(role) ? "mine" : "all";
	const filter: Filter = search.filter ?? defaultFilter;
	const today = algiersToday();

	const query = useQuery(trpc.degrees.list.queryOptions());
	const rows = query.data ?? [];

	const matches: Record<Filter, (row: (typeof rows)[number]) => boolean> = {
		mine: (row) =>
			row.degree ? degreeWaitsOn(role, row.degree.status) : false,
		all: () => true,
		REQUESTED: (row) => row.degree?.status === "REQUESTED",
		IN_PROGRESS: (row) => row.degree?.status === "IN_PROGRESS",
		DELIVERED: (row) => row.degree?.status === "DELIVERED",
		none: (row) => !row.degree,
	};

	const tabs: TabItem<Filter>[] = [
		{ key: "mine", label: "بانتظارك", count: rows.filter(matches.mine).length },
		{ key: "all", label: "الكل", count: rows.length },
		{
			key: "REQUESTED",
			label: DEGREE_STATUS_LABELS.REQUESTED,
			count: rows.filter(matches.REQUESTED).length,
		},
		{
			key: "IN_PROGRESS",
			label: DEGREE_STATUS_LABELS.IN_PROGRESS,
			count: rows.filter(matches.IN_PROGRESS).length,
		},
		{
			key: "DELIVERED",
			label: DEGREE_STATUS_LABELS.DELIVERED,
			count: rows.filter(matches.DELIVERED).length,
		},
		{
			key: "none",
			label: "بدون قائمة",
			count: rows.filter(matches.none).length,
		},
	];

	function setSearch(next: DegreesSearch) {
		void navigate({ search: next, replace: true });
	}

	const intro = canEditDegreeFiles(role)
		? canEditDegreeStudents(role)
			? "قائمة المتخرّجين في كل طبعة والشهادات المنجزة لهم."
			: "أنجز شهادات المتخرّجين في كل طبعة: ارفع شهادة لكل طالب، أو شارك رابط مجلد يضمّها كلها."
		: "أدخل المتخرّجين في كل طبعة كما يجب أن تُكتب أسماؤهم، ليُنجز المصمّم شهاداتهم.";

	// The list comes most recent run first; this filter keeps that order.
	const visible = rows.filter(matches[filter]);

	return (
		<div className="flex flex-col gap-5">
			<div>
				<h1 className="text-2xl font-bold text-navy">الشهادات</h1>
				<p className="text-sm text-muted">{intro}</p>
			</div>

			<TabBar
				tabs={tabs}
				value={filter}
				onChange={(key) =>
					setSearch({
						...search,
						filter: key === defaultFilter ? undefined : key,
					})
				}
			/>

			{query.isLoading ? (
				<div className={`flex justify-center py-16 ${CARD}`}>
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError ? (
				<p className={`px-6 py-10 text-center text-sm text-red-600 ${CARD}`}>
					{query.error.message}
				</p>
			) : visible.length === 0 ? (
				<p className={`px-6 py-10 text-center text-sm text-muted ${CARD}`}>
					{filter === "mine"
						? "لا شيء بانتظارك الآن."
						: "لا توجد طبعات في هذه القائمة."}
				</p>
			) : (
				<ul className={`divide-y divide-line ${CARD}`}>
					{visible.map((row) => {
						const due = row.degree?.dueDate ? isoDay(row.degree.dueDate) : null;
						const late =
							due !== null && due < today && row.degree?.status !== "DELIVERED";
						return (
							<li key={row.courseNumber}>
								<button
									type="button"
									onClick={() =>
										setSearch({ ...search, edition: row.courseNumber })
									}
									className="flex w-full flex-col gap-2 px-5 py-4 text-start transition-colors hover:bg-canvas/60 sm:flex-row sm:items-center sm:gap-4"
								>
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-bold text-navy">
												{row.training.name}
											</span>
											<span className="text-sm font-bold text-muted">
												{editionLabel(row.editionNumber)}
											</span>
											<StatusBadge status={row.degree?.status ?? null} />
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
											<span>{runLabel(row.startDate, row.endDate)}</span>
											{row.degree ? (
												<>
													<span className="inline-flex items-center gap-1">
														<Users className="h-3.5 w-3.5" />
														{row.degree.students} طالب
													</span>
													<span className="inline-flex items-center gap-1">
														<FileCheck2 className="h-3.5 w-3.5" />
														{row.degree.files} شهادة
													</span>
													{row.degree.hasDriveUrl ? (
														<span className="inline-flex items-center gap-1">
															<FolderOpen className="h-3.5 w-3.5" />
															مجلد مشترك
														</span>
													) : null}
												</>
											) : null}
											{row.confirmed !== null && row.confirmed > 0 ? (
												<span className="inline-flex items-center gap-1">
													<UserCheck className="h-3.5 w-3.5" />
													{row.confirmed} طلب مؤكّد
												</span>
											) : null}
										</div>
									</div>
									{due ? (
										<span
											className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-bold ${late ? "text-red-600" : "text-navy"}`}
										>
											<CalendarClock className="h-4 w-4" />
											{late ? "متأخر — " : "مطلوب قبل "}
											{dayLabel(due)}
										</span>
									) : null}
								</button>
							</li>
						);
					})}
				</ul>
			)}

			{search.edition !== undefined ? (
				<DegreeDialog
					editionId={search.edition}
					role={role}
					onClose={() => setSearch({ ...search, edition: undefined })}
				/>
			) : null}
		</div>
	);
}

function DegreeDialog({
	editionId,
	role,
	onClose,
}: {
	editionId: number;
	role: string | null | undefined;
	onClose: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const queryClient = useQueryClient();
	const query = useQuery(trpc.degrees.get.queryOptions({ editionId }));
	const [error, setError] = useState<string | null>(null);

	function refresh() {
		void queryClient.invalidateQueries({
			queryKey: trpc.degrees.get.queryKey({ editionId }),
		});
		void queryClient.invalidateQueries({
			queryKey: trpc.degrees.list.queryKey(),
		});
	}

	const statusMutation = useMutation({
		...trpc.degrees.setStatus.mutationOptions(),
		onSuccess: refresh,
		onError: (err) => setError(err.message),
	});

	const edition = query.data;
	const degree = edition?.degree ?? null;
	const students = degree?.students ?? [];
	const actions = degree
		? degreeActions(role, degree.status, {
				students: students.length,
				files: students.filter((s) => s.file).length,
				hasDriveUrl: Boolean(degree.driveUrl),
			})
		: [];
	const editsStudents = canEditDegreeStudents(role);
	const editsFiles = canEditDegreeFiles(role);

	return (
		<Modal
			title={
				edition
					? `${edition.training.name} — ${editionLabel(edition.editionNumber)}`
					: "الشهادات"
			}
			subtitle={
				edition ? (
					<span className="flex flex-wrap items-center gap-2">
						<StatusBadge status={degree?.status ?? null} />
						<span>{runLabel(edition.startDate, edition.endDate)}</span>
						{degree?.requestedBy ? (
							<span>· طلبها {degree.requestedBy.name}</span>
						) : null}
						{degree?.deliveredAt ? (
							<span>· سُلّمت {formatDateTime(degree.deliveredAt)}</span>
						) : null}
					</span>
				) : null
			}
			width="xl"
			onClose={onClose}
			footer={
				<div className="flex flex-wrap items-center justify-between gap-2">
					{error ? (
						<p className="text-sm font-semibold text-red-600">{error}</p>
					) : (
						<span />
					)}
					<div className="flex flex-wrap gap-2">
						<GhostButton onClick={onClose}>إغلاق</GhostButton>
						{actions.map((action) => (
							<PrimaryButton
								key={action.to}
								disabled={statusMutation.isPending}
								onClick={() => {
									setError(null);
									statusMutation.mutate({ editionId, status: action.to });
								}}
							>
								{statusMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Check className="h-4 w-4" />
								)}
								{action.label}
							</PrimaryButton>
						))}
					</div>
				</div>
			}
		>
			{query.isLoading ? (
				<div className="flex justify-center py-10">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError || !edition ? (
				<p className="py-10 text-center text-sm text-red-600">
					{query.error?.message ?? "الطبعة غير موجودة"}
				</p>
			) : (
				<div className="flex flex-col gap-6">
					<div className="grid gap-4 lg:grid-cols-2">
						<BriefEditor
							editionId={editionId}
							brief={degree?.brief ?? null}
							dueDate={degree?.dueDate ? isoDay(degree.dueDate) : null}
							canEdit={editsStudents}
							onSaved={refresh}
						/>
						{degree ? (
							<DeliveryEditor
								editionId={editionId}
								driveUrl={degree.driveUrl}
								note={degree.designNote}
								canEdit={editsFiles}
								onSaved={refresh}
							/>
						) : (
							<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
								تظهر الشهادات هنا بعد أن يُدخل فريق المبيعات قائمة المتخرّجين.
							</p>
						)}
					</div>

					<section className="flex flex-col gap-3">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<h3 className="flex items-center gap-2 font-bold text-navy">
								<Users className="h-4.5 w-4.5" />
								المتخرّجون
								<span className="text-sm text-muted">({students.length})</span>
							</h3>
							{editsStudents ? (
								<ImportButton
									editionId={editionId}
									importable={edition.importable ?? 0}
									onDone={refresh}
								/>
							) : null}
						</div>

						<StudentList
							students={students}
							editsStudents={editsStudents}
							editsFiles={editsFiles}
							onChanged={refresh}
						/>

						{editsStudents ? (
							<AddStudents editionId={editionId} onAdded={refresh} />
						) : null}
					</section>
				</div>
			)}
		</Modal>
	);
}

/* -------------------------------------------------------------------------- */
/*  Students                                                                  */
/* -------------------------------------------------------------------------- */

interface StudentRow {
	id: string;
	fullName: string;
	latinName: string | null;
	birthDate: string | Date | null;
	birthPlace: string | null;
	note: string | null;
	file: {
		id: string;
		name: string;
		contentType: string;
		size: number;
		createdAt: string | Date;
		uploadedBy: { name: string } | null;
	} | null;
}

function fileUrl(id: string, download = false) {
	return `/api/admin/degree-file/${id}${download ? "?download=1" : ""}`;
}

const ICON_BUTTON =
	"rounded-full p-2 text-muted transition-colors hover:bg-canvas hover:text-navy";

function StudentList({
	students,
	editsStudents,
	editsFiles,
	onChanged,
}: {
	students: StudentRow[];
	editsStudents: boolean;
	editsFiles: boolean;
	onChanged: () => void;
}) {
	const [editing, setEditing] = useState<string | null>(null);

	if (!students.length) {
		return (
			<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
				{editsStudents
					? "لا يوجد متخرّجون بعد — استورد الطلبات المؤكّدة أو أضفهم أدناه."
					: "لم تُدخل قائمة المتخرّجين بعد."}
			</p>
		);
	}

	return (
		<ol className="flex flex-col divide-y divide-line rounded-xl border border-line">
			{students.map((student, index) =>
				editing === student.id ? (
					<li key={student.id} className="p-3">
						<StudentEditor
							student={student}
							onDone={() => {
								setEditing(null);
								onChanged();
							}}
							onCancel={() => setEditing(null)}
						/>
					</li>
				) : (
					<StudentItem
						key={student.id}
						index={index}
						student={student}
						editsStudents={editsStudents}
						editsFiles={editsFiles}
						onEdit={() => setEditing(student.id)}
						onChanged={onChanged}
					/>
				),
			)}
		</ol>
	);
}

function StudentItem({
	index,
	student,
	editsStudents,
	editsFiles,
	onEdit,
	onChanged,
}: {
	index: number;
	student: StudentRow;
	editsStudents: boolean;
	editsFiles: boolean;
	onEdit: () => void;
	onChanged: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [confirming, setConfirming] = useState<"student" | "file" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const done = {
		onSuccess: () => {
			setConfirming(null);
			onChanged();
		},
		onError: (err: { message: string }) => setError(err.message),
	};
	const removeStudent = useMutation({
		...trpc.degrees.removeStudent.mutationOptions(),
		...done,
	});
	const removeFile = useMutation({
		...trpc.degrees.removeFile.mutationOptions(),
		...done,
	});

	const details = [
		student.birthDate
			? `مولود في ${dayLabel(isoDay(student.birthDate))}`
			: null,
		student.birthPlace ? `بـ ${student.birthPlace}` : null,
	].filter(Boolean);

	return (
		<li className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-4">
			<div className="flex min-w-0 flex-1 gap-3">
				<span className="w-6 shrink-0 pt-0.5 text-center text-xs font-bold text-muted">
					{index + 1}
				</span>
				<div className="min-w-0 flex-1">
					<p className="font-bold text-navy" dir="auto">
						{student.fullName}
					</p>
					{student.latinName ? (
						<p className="text-sm text-navy" dir="ltr">
							{student.latinName}
						</p>
					) : null}
					{details.length ? (
						<p className="text-xs text-muted">{details.join(" ")}</p>
					) : null}
					{student.note ? (
						<p className="text-xs text-muted" dir="auto">
							{student.note}
						</p>
					) : null}
					{error ? (
						<p className="text-xs font-semibold text-red-600">{error}</p>
					) : null}
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-1 ps-9 sm:ps-0">
				{student.file ? (
					<>
						<span
							className="inline-flex max-w-48 items-center gap-1 truncate rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"
							title={`${student.file.name} · ${formatBytes(student.file.size)}`}
						>
							<FileCheck2 className="h-3.5 w-3.5 shrink-0" />
							الشهادة
						</span>
						{isPreviewable(student.file.contentType) ? (
							<a
								href={fileUrl(student.file.id)}
								target="_blank"
								rel="noreferrer"
								className={ICON_BUTTON}
								aria-label="فتح الشهادة"
								title="فتح الشهادة"
							>
								<ExternalLink className="h-4 w-4" />
							</a>
						) : null}
						<a
							href={fileUrl(student.file.id, true)}
							className={ICON_BUTTON}
							aria-label="تحميل الشهادة"
							title="تحميل الشهادة"
						>
							<Download className="h-4 w-4" />
						</a>
					</>
				) : (
					<span className="rounded-full bg-canvas px-2.5 py-1 text-xs font-bold text-muted">
						بدون شهادة
					</span>
				)}

				{editsFiles ? (
					<CertificateUpload
						studentId={student.id}
						replacing={Boolean(student.file)}
						onUploaded={onChanged}
						onError={setError}
					/>
				) : null}

				{confirming ? (
					<span className="flex items-center gap-1">
						<button
							type="button"
							disabled={removeStudent.isPending || removeFile.isPending}
							onClick={() => {
								setError(null);
								if (confirming === "student") {
									removeStudent.mutate({ studentId: student.id });
								} else if (student.file) {
									removeFile.mutate({ fileId: student.file.id });
								}
							}}
							className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
						>
							{confirming === "student" ? "حذف الطالب" : "حذف الشهادة"}
						</button>
						<button
							type="button"
							onClick={() => setConfirming(null)}
							className="rounded-full px-2 py-1.5 text-xs font-bold text-muted hover:text-navy"
						>
							إلغاء
						</button>
					</span>
				) : (
					<>
						{editsFiles && student.file ? (
							<button
								type="button"
								onClick={() => setConfirming("file")}
								className="rounded-full p-2 text-muted transition-colors hover:bg-red-50 hover:text-red-600"
								aria-label="حذف الشهادة"
								title="حذف الشهادة"
							>
								<Trash2 className="h-4 w-4" />
							</button>
						) : null}
						{editsStudents ? (
							<>
								<button
									type="button"
									onClick={onEdit}
									className={ICON_BUTTON}
									aria-label="تعديل بيانات الطالب"
									title="تعديل بيانات الطالب"
								>
									<Pencil className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={() => setConfirming("student")}
									className="rounded-full p-2 text-muted transition-colors hover:bg-red-50 hover:text-red-600"
									aria-label="حذف الطالب"
									title="حذف الطالب"
								>
									<UserMinus className="h-4 w-4" />
								</button>
							</>
						) : null}
					</>
				)}
			</div>
		</li>
	);
}

function CertificateUpload({
	studentId,
	replacing,
	onUploaded,
	onError,
}: {
	studentId: string;
	replacing: boolean;
	onUploaded: () => void;
	onError: (message: string | null) => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [busy, setBusy] = useState(false);

	async function upload(file: File) {
		onError(null);
		const invalid = validateDegreeUpload(file);
		if (invalid) {
			onError(invalid);
			return;
		}
		setBusy(true);
		try {
			const body = new FormData();
			body.append("file", file);
			body.append("studentId", studentId);
			const response = await fetch("/api/admin/degree-upload", {
				method: "POST",
				body,
			});
			const payload = (await response.json().catch(() => null)) as {
				error?: string;
			} | null;
			if (!response.ok) {
				onError(payload?.error ?? "تعذّر رفع الملف. حاول مجدداً.");
				return;
			}
			onUploaded();
		} catch {
			onError("تعذّر الاتصال بالخادم. حاول مجدداً.");
		} finally {
			setBusy(false);
			if (inputRef.current) inputRef.current.value = "";
		}
	}

	const label = replacing ? "استبدال الشهادة" : "رفع الشهادة";
	return (
		<>
			<input
				ref={inputRef}
				type="file"
				accept={DEGREE_ACCEPT_ATTR}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) void upload(file);
				}}
			/>
			<button
				type="button"
				disabled={busy}
				onClick={() => inputRef.current?.click()}
				className={
					replacing
						? ICON_BUTTON
						: "inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-canvas disabled:opacity-50"
				}
				aria-label={label}
				title={label}
			>
				{busy ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Upload className="h-4 w-4" />
				)}
				{replacing ? null : label}
			</button>
		</>
	);
}

interface StudentFields {
	fullName: string;
	latinName: string;
	birthDate: string;
	birthPlace: string;
	note: string;
}

const EMPTY_FIELDS: StudentFields = {
	fullName: "",
	latinName: "",
	birthDate: "",
	birthPlace: "",
	note: "",
};

function StudentFieldsForm({
	value,
	onChange,
}: {
	value: StudentFields;
	onChange: (next: StudentFields) => void;
}) {
	const field = (key: keyof StudentFields) => ({
		value: value[key],
		onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			onChange({ ...value, [key]: e.target.value }),
	});
	return (
		<div className="grid gap-3 sm:grid-cols-2">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				الاسم الكامل كما يُكتب في الشهادة *
				<input
					{...field("fullName")}
					maxLength={200}
					dir="auto"
					className={INPUT}
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				الاسم بالحروف اللاتينية
				<input
					{...field("latinName")}
					maxLength={200}
					dir="ltr"
					className={INPUT}
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				تاريخ الميلاد
				<input type="date" {...field("birthDate")} className={INPUT} />
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				مكان الميلاد
				<input
					{...field("birthPlace")}
					maxLength={200}
					dir="auto"
					className={INPUT}
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted sm:col-span-2">
				ملاحظة للمصمّم
				<input
					{...field("note")}
					maxLength={1000}
					dir="auto"
					placeholder="تقدير، إشارة خاصة…"
					className={INPUT}
				/>
			</label>
		</div>
	);
}

function toInput(fields: StudentFields) {
	return {
		fullName: fields.fullName.trim(),
		latinName: fields.latinName.trim() || null,
		birthDate: fields.birthDate || null,
		birthPlace: fields.birthPlace.trim() || null,
		note: fields.note.trim() || null,
	};
}

function StudentEditor({
	student,
	onDone,
	onCancel,
}: {
	student: StudentRow;
	onDone: () => void;
	onCancel: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [fields, setFields] = useState<StudentFields>({
		fullName: student.fullName,
		latinName: student.latinName ?? "",
		birthDate: student.birthDate ? isoDay(student.birthDate) : "",
		birthPlace: student.birthPlace ?? "",
		note: student.note ?? "",
	});
	const [error, setError] = useState<string | null>(null);
	const mutation = useMutation({
		...trpc.degrees.updateStudent.mutationOptions(),
		onSuccess: onDone,
		onError: (err) => setError(err.message),
	});

	return (
		<div className="flex flex-col gap-3">
			<StudentFieldsForm value={fields} onChange={setFields} />
			<div className="flex flex-wrap items-center gap-2">
				<GhostButton
					disabled={!fields.fullName.trim() || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({ studentId: student.id, ...toInput(fields) });
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Check className="h-4 w-4" />
					)}
					حفظ
				</GhostButton>
				<GhostButton onClick={onCancel}>إلغاء</GhostButton>
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}

function ImportButton({
	editionId,
	importable,
	onDone,
}: {
	editionId: number;
	importable: number;
	onDone: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [error, setError] = useState<string | null>(null);
	const mutation = useMutation({
		...trpc.degrees.importConfirmed.mutationOptions(),
		onSuccess: onDone,
		onError: (err) => setError(err.message),
	});

	if (importable === 0) return null;
	return (
		<div className="flex flex-col items-end gap-1">
			<GhostButton
				disabled={mutation.isPending}
				onClick={() => {
					setError(null);
					mutation.mutate({ editionId });
				}}
			>
				{mutation.isPending ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<UserCheck className="h-4 w-4" />
				)}
				استيراد الطلبات المؤكّدة ({importable})
			</GhostButton>
			{error ? (
				<p className="text-xs font-semibold text-red-600">{error}</p>
			) : null}
		</div>
	);
}

function AddStudents({
	editionId,
	onAdded,
}: {
	editionId: number;
	onAdded: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [mode, setMode] = useState<"closed" | "one" | "paste">("closed");
	const [fields, setFields] = useState<StudentFields>(EMPTY_FIELDS);
	const [pasted, setPasted] = useState("");
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		...trpc.degrees.addStudents.mutationOptions(),
		onSuccess: () => {
			setFields(EMPTY_FIELDS);
			setPasted("");
			setMode("closed");
			onAdded();
		},
		onError: (err) => setError(err.message),
	});

	const parsed = mode === "paste" ? parseStudentLines(pasted) : null;
	const badDate =
		fields.birthDate !== "" && parseBirthDate(fields.birthDate) === null;

	if (mode === "closed") {
		return (
			<div className="flex flex-wrap gap-2">
				<GhostButton onClick={() => setMode("one")}>
					<Plus className="h-4 w-4" />
					إضافة طالب
				</GhostButton>
				<GhostButton onClick={() => setMode("paste")}>
					<ClipboardPaste className="h-4 w-4" />
					لصق قائمة
				</GhostButton>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			{mode === "one" ? (
				<StudentFieldsForm value={fields} onChange={setFields} />
			) : (
				<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
					طالب في كل سطر — انسخ الأعمدة من Excel، أو افصل بينها بـ «;»: الاسم،
					الاسم باللاتينية، تاريخ الميلاد (يوم/شهر/سنة)، مكان الميلاد. الاسم
					وحده إلزامي.
					<textarea
						value={pasted}
						onChange={(e) => setPasted(e.target.value)}
						rows={6}
						dir="auto"
						placeholder={
							"أمين بن علي;Amine Benali;05/03/1998;الجزائر\nسارة قاسم"
						}
						className={INPUT}
					/>
				</label>
			)}

			{parsed?.errors.length ? (
				<ul className="text-xs font-semibold text-red-600">
					{parsed.errors.slice(0, 5).map((message) => (
						<li key={message}>{message}</li>
					))}
				</ul>
			) : null}

			<div className="flex flex-wrap items-center gap-2">
				<GhostButton
					disabled={
						mutation.isPending ||
						(mode === "one"
							? !fields.fullName.trim() || badDate
							: !parsed?.students.length || parsed.errors.length > 0)
					}
					onClick={() => {
						setError(null);
						mutation.mutate({
							editionId,
							students:
								mode === "one" ? [toInput(fields)] : (parsed?.students ?? []),
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Plus className="h-4 w-4" />
					)}
					{mode === "one"
						? "إضافة"
						: `إضافة ${parsed?.students.length ?? 0} طالب`}
				</GhostButton>
				<GhostButton
					onClick={() => {
						setMode("closed");
						setError(null);
					}}
				>
					إلغاء
				</GhostButton>
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Brief (sales) and delivery (designer)                                     */
/* -------------------------------------------------------------------------- */

function BriefEditor({
	editionId,
	brief,
	dueDate,
	canEdit,
	onSaved,
}: {
	editionId: number;
	brief: string | null;
	dueDate: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [text, setText] = useState(brief ?? "");
	const [due, setDue] = useState(dueDate ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Pick up what the server holds whenever it changes under us.
	useEffect(() => {
		setText(brief ?? "");
		setDue(dueDate ?? "");
	}, [brief, dueDate]);

	const mutation = useMutation({
		...trpc.degrees.saveBrief.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	if (!canEdit) {
		return (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">توجيهات للمصمّم</p>
				<p className="whitespace-pre-line text-sm text-navy" dir="auto">
					{brief || "لا توجد توجيهات."}
				</p>
				{dueDate ? (
					<p className="inline-flex items-center gap-1.5 text-xs font-bold text-navy">
						<CalendarClock className="h-4 w-4" />
						مطلوب قبل {dayLabel(dueDate)}
					</p>
				) : null}
			</div>
		);
	}

	const dirty = text !== (brief ?? "") || due !== (dueDate ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				توجيهات للمصمّم
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={3}
					maxLength={4000}
					dir="auto"
					placeholder="النموذج المعتمد، اللغة، عدد الساعات المذكور، أي ملاحظة."
					className={INPUT}
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				مطلوب قبل
				<input
					type="date"
					value={due}
					onChange={(e) => {
						setDue(e.target.value);
						setSaved(false);
					}}
					className={`${INPUT} sm:w-48`}
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({
							editionId,
							brief: text.trim() || null,
							dueDate: due || null,
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ التوجيهات
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}

function DeliveryEditor({
	editionId,
	driveUrl,
	note,
	canEdit,
	onSaved,
}: {
	editionId: number;
	driveUrl: string | null;
	note: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [url, setUrl] = useState(driveUrl ?? "");
	const [text, setText] = useState(note ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setUrl(driveUrl ?? "");
		setText(note ?? "");
	}, [driveUrl, note]);

	const mutation = useMutation({
		...trpc.degrees.saveDelivery.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	const folderLink = driveUrl ? (
		<a
			href={driveUrl}
			target="_blank"
			rel="noreferrer noopener"
			className="inline-flex items-center gap-1.5 self-start rounded-full bg-teal-50 px-3 py-1.5 text-sm font-bold text-teal-600 hover:bg-teal-100"
		>
			<FolderOpen className="h-4 w-4" />
			فتح مجلد الشهادات
		</a>
	) : null;

	if (!canEdit) {
		return (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">التسليم</p>
				{folderLink ?? (
					<p className="text-sm text-muted">
						لا يوجد مجلد مشترك — الشهادات مرفوعة لكل طالب أدناه.
					</p>
				)}
				{note ? (
					<p className="whitespace-pre-line text-sm text-navy" dir="auto">
						{note}
					</p>
				) : null}
			</div>
		);
	}

	const dirty = url !== (driveUrl ?? "") || text !== (note ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				رابط مجلد يضمّ كل الشهادات (Google Drive…)
				<input
					type="url"
					value={url}
					onChange={(e) => {
						setUrl(e.target.value);
						setSaved(false);
					}}
					dir="ltr"
					placeholder="https://drive.google.com/drive/folders/…"
					className={INPUT}
				/>
				<span className="font-normal">
					بديل عن رفع شهادة لكل طالب. تأكّد أن الرابط مفتوح لفريق المبيعات.
				</span>
			</label>
			{folderLink}
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				ملاحظة لفريق المبيعات
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={2}
					maxLength={4000}
					dir="auto"
					className={INPUT}
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({
							editionId,
							driveUrl: url.trim() || null,
							designNote: text.trim() || null,
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}
